import { ChangePassword, Login } from "@bs/login";
import { locale as setPRLocale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import packageJson from '../package.json';
import './App.css';
import { PrivateRoute } from "./components";
import LoadingPanel from "./components/LoadingPanel";
import { whereIsThisContext } from "./contexts/WhereIsThisContext";
import HandleCache from "./HandleCache";
import './i18n';
import './locales/primereact-locale';
import { authenticationService, startupConfigurationService } from "./services";

// Yeni oluşturduğumuz utility dosyasından gerekli fonksiyonları çağırıyoruz
import { checkPermission, checkPermissions, clearPermissionsCache, loadPermissions } from './utils/permissionUtils';

// Global tema ve ortak stiller. Bunlar önceden LayerControlSettingsMainComponent import zinciri
// (CustomerTable, LayerControl) üzerinden dolaylı olarak ana bundle'a giriyordu. Bileşen lazy
// yüklendiği için login ekranı temasız kalmasın diye aynı sırayla burada açıkça import edilir.
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'ol/ol.css';
import './components/LayerControlPage/LayerControl.css';
import './bsMapGlobalStyles.css';

// Lazy Loading Optimizasyonları
// webpackPrefetch: kullanıcı login ekranındayken tarayıcı boşta harita chunk'ını önceden indirir.
const HomePage = lazy(() =>
    import(/* webpackPrefetch: true */ "./pages").then((module) => ({ default: module.HomePage }))
);

// Yönetim sayfası harita kodunu (@bs/map, LayerControl, CustomerTable) içerdiği için ana bundle'dan ayrılır.
const LayerControlSettingsMainComponent = lazy(() =>
    import("./components/LayerControlManagementPage/LayerControlSettingsMainComponent")
);


const baseUrl = process.env.PUBLIC_URL;

const LoginPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [selectedLanguage, setSelectedLanguage] = useState(() => localStorage.getItem('lang') || i18n.language || 'tr');

    useEffect(() => {
        // 1. Silinmesini istemediğimiz verileri geçici olarak tutacağımız nesne
        const preservedItems = {};

        // 2. localStorage içindeki tüm anahtarları dön ve '-username-rm' içerenleri bul
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes('-username-rm')) {
                preservedItems[key] = localStorage.getItem(key);
            }
        }

        // 3. Token ve diğer tüm verileri temizle
        localStorage.clear();
        clearPermissionsCache();

        // 4. Koruduğumuz verileri localStorage'a geri yükle
        Object.keys(preservedItems).forEach(key => {
            localStorage.setItem(key, preservedItems[key]);
        });
    }, []);

    const languages = [
        { label: 'Türkçe', value: 'tr' },
        { label: 'English', value: 'en' }
    ];

    const changeLanguage = (lng) => {
        setSelectedLanguage(lng);
        i18n.changeLanguage(lng);
        localStorage.setItem('lang', lng);
        setPRLocale(lng === 'tr' ? 'tr' : 'en');
    };

    // Temiz Asenkron Kod (Async/Await) ve Hata Yönetimi
    const handleLoginSuccess = async (data) => {
        try {
            localStorage.setItem(process.env.REACT_APP_LS_TOKEN, data.token);

            const userResponse = await authenticationService.getUserByToken(process.env.REACT_APP_USER_API_URL);
            localStorage.setItem(process.env.REACT_APP_LS_USER, JSON.stringify(userResponse.data));

            // Kullanıcı verisi alındıktan sonra bellek cache'ini güncelle
            loadPermissions();

            const configResponse = await startupConfigurationService.getStartupConfiguration(process.env.REACT_APP_STARTUP_CONFIGURATION_API_URL);
            localStorage.setItem("startupconfiguration", JSON.stringify(configResponse.data));

            navigate("/");
        } catch (ex) {
            toast.error(t('err_001_001_001'));
        }
    };

    return (<>
        <Button
            tooltip={t('homepage.menu.user_guide')}
            style={{ position: "absolute" }}
            icon="pi pi-question-circle"
            link
            onClick={() => window.open(process.env.REACT_APP_USER_MANUEL + "/docs/login", '_blank')}
        />
        <Login
            t={t}
            url={process.env.REACT_APP_USER_API_URL}
            onSuccess={handleLoginSuccess}
            localStorageUserKey={process.env.REACT_APP_LS_USER}
            theme="left"
            logo={`${process.env.PUBLIC_URL}/group_83.svg`}
            appName={t('lbl_001_001_001')}
            rememberMe={true}
            captcha={false}
            image={`${process.env.PUBLIC_URL}/basarsoft-cbs-hmp1.jpg`}
            version={"v" + packageJson.version}
            forgotPassword={true}
        />

        <div style={{ right: '1vw', top: '4vh', width: '4vw', minWidth: '120px', position: 'absolute' }}>
            <Dropdown
                value={selectedLanguage}
                options={languages}
                onChange={(e) => changeLanguage(e.value)}
                placeholder={t('lbl_001_001_002') || 'Dil Seçin'}
                className="w-full"
                emptyMessage={t('common.no_options')}
                emptyFilterMessage={t('common.no_options')}
            />
        </div>
    </>);


}

//Tekil kontroller için kalmalı
window.Object.checkPermission = (permission) => {

    

    return checkPermission(permission);
}

window.Object.checkPermissions = (permissions) => {
    
    return checkPermissions(permissions);
}


const App = () => {
    const [whereIsState] = useContext(whereIsThisContext);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        let lang = localStorage.getItem('lang');
        if (!lang) {
            lang = 'tr';
            localStorage.setItem('lang', lang);
        }

        i18n.changeLanguage(lang);

        const handler = (lng) => {
            const short = (lng || 'tr').split('-')[0];
            setPRLocale(short === 'tr' ? 'tr' : 'en');
        };

        handler(lang);
        i18n.on('languageChanged', handler);

        // App ilk yüklendiğinde (kullanıcı zaten giriş yapmışsa) bellek cache'ini doldur
        loadPermissions();

        return () => i18n.off('languageChanged', handler);
    }, [i18n]);


    return (
        <HandleCache>
            {({ isLoading, isLatestVersionAvailable, clearCacheAndReload }) => {
                if (isLoading) return null;

                if (!isLoading && !isLatestVersionAvailable) {
                    clearCacheAndReload();
                }

                return (
                    <div className={!whereIsState.isLoading ? "container" : "overlay"}>
                        <div id={!whereIsState.isLoading ? "" : "blur"}></div>
                        {whereIsState.isLoading && <LoadingPanel />}
                        <ToastContainer />
                        <BrowserRouter basename={baseUrl}>
                            <Suspense fallback={<LoadingPanel />}>
                                <Routes>
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/reset-password" element={
                                        <ChangePassword url={process.env.REACT_APP_USER_API_URL} t={t} />} />
                                    <Route path="/" element={
                                        <PrivateRoute redirectPath="/login">
                                            <HomePage />
                                        </PrivateRoute>} />
                                    <Route path="*" element={<LoginPage />} />
                                    <Route path="/layerControlSettings" element={<LayerControlSettingsMainComponent />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </div>
                )
            }}
        </HandleCache >
    );
}

export default App;
